import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import LanguageSwitcher from "@/components/LanguageSwitcher";

export const PRIVACY_VERSION = "v1.2026-05";

export default function Privacy() {
  const { t } = useTranslation();
  return (
    <div className="relative min-h-screen text-ca-ink">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="max-w-3xl mx-auto px-6 py-12 leading-relaxed">
        <nav className="mb-8 text-sm text-ca-ink-muted">
          <Link to="/" className="hover:text-ca-accent-text">
            ← Nudging Bloom
          </Link>
        </nav>

        <h1 className="text-3xl font-bold text-ca-ink mb-2">{t("privacy.title")}</h1>
        <p className="text-ca-ink-muted text-sm mb-10">
          <Trans
            i18nKey="privacy.forApp"
            components={{ b: <strong className="text-ca-ink" /> }}
          />
        </p>

        <Section title={t("privacy.s1.title")}>
          <address className="not-italic">
            Dr. rer. medic. Franz Korbinian Hütter, M.A.
            <br />
            BRAIN-HR Dr. Franz Hütter
            <br />
            Am Schlagbaum 1, 58285 Gevelsberg
            <br />
            E-Mail:{" "}
            <ExtA href="mailto:fh@brain-hr.com">fh@brain-hr.com</ExtA>
            <br />
            Telefon: +49 (0)171 99 79 519
          </address>
        </Section>

        <Section title={t("privacy.s2.title")}>
          <P>
            <Trans
              i18nKey="privacy.s2.p1"
              components={{ b: <strong /> }}
            />
          </P>
          <P>
            <Trans
              i18nKey="privacy.s2.p2"
              components={{ b: <strong /> }}
            />
          </P>
        </Section>

        <Section title={t("privacy.s3.title")}>
          <H3>{t("privacy.s3.aTitle")}</H3>
          <table className="w-full text-sm border-collapse my-3">
            <thead>
              <tr className="text-ca-ink-muted">
                <th className="text-left py-2 pr-3 border-b border-ca-border font-semibold">
                  {t("privacy.s3.th1")}
                </th>
                <th className="text-left py-2 pr-3 border-b border-ca-border font-semibold">
                  {t("privacy.s3.th2")}
                </th>
                <th className="text-left py-2 border-b border-ca-border font-semibold">
                  {t("privacy.s3.th3")}
                </th>
              </tr>
            </thead>
            <tbody className="text-ca-ink">
              <Tr
                a={t("privacy.s3.r1a")}
                b={t("privacy.s3.r1b")}
                c={t("privacy.s3.r1c")}
              />
              <Tr
                a={t("privacy.s3.r2a")}
                b={t("privacy.s3.r2b")}
                c={t("privacy.s3.r2c")}
              />
              <Tr
                a={t("privacy.s3.r3a")}
                b={t("privacy.s3.r3b")}
                c={t("privacy.s3.r3c")}
              />
              <Tr
                a={t("privacy.s3.r4a")}
                b={t("privacy.s3.r4b")}
                c={t("privacy.s3.r4c")}
              />
              <Tr
                a={t("privacy.s3.r5a")}
                b={t("privacy.s3.r5b")}
                c={t("privacy.s3.r5c")}
              />
              <Tr
                a={t("privacy.s3.r6a")}
                b={t("privacy.s3.r6b")}
                c={
                  <Trans
                    i18nKey="privacy.s3.r6c"
                    components={{
                      a: <ExtA href="mailto:fh@brain-hr.com">fh@brain-hr.com</ExtA>,
                    }}
                  />
                }
              />
            </tbody>
          </table>

          <H3>{t("privacy.s3.bTitle")}</H3>
          <P>
            <Trans i18nKey="privacy.s3.bBody" components={{ em: <em /> }} />
          </P>
        </Section>

        <Section title={t("privacy.s4.title")}>
          <H3>{t("privacy.s4.hostingTitle")}</H3>
          <P>
            <Trans i18nKey="privacy.s4.hostingBody" components={{ b: <strong /> }} />
          </P>

          <H3>{t("privacy.s4.aiTitle")}</H3>
          <P>
            <Trans i18nKey="privacy.s4.aiBody1" components={{ b: <strong /> }} />
          </P>
          <P>
            <Trans
              i18nKey="privacy.s4.aiBody2"
              components={{
                a: (
                  <ExtA
                    href="https://www.anthropic.com/legal/data-processing-addendum"
                    external
                  >
                    Anthropic Data Processing Addendum
                  </ExtA>
                ),
              }}
            />
          </P>
          <P>
            <Trans
              i18nKey="privacy.s4.aiBody3"
              components={{
                a1: (
                  <ExtA href="https://www.anthropic.com/subprocessors" external>
                    anthropic.com/subprocessors
                  </ExtA>
                ),
                a2: (
                  <ExtA href="https://www.anthropic.com/legal/privacy" external>
                    anthropic.com/legal/privacy
                  </ExtA>
                ),
              }}
            />
          </P>

          <H3>{t("privacy.s4.crmTitle")}</H3>
          <P>{t("privacy.s4.crmBody")}</P>
        </Section>

        <Section title={t("privacy.s5.title")}>
          <ul className="list-disc ml-6 space-y-1">
            <li>
              <Trans i18nKey="privacy.s5.li1" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s5.li2" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s5.li3" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s5.li4" components={{ b: <strong /> }} />
            </li>
          </ul>
        </Section>

        <Section title={t("privacy.s6.title")}>
          <P>{t("privacy.s6.intro")}</P>
          <ul className="list-disc ml-6 space-y-1">
            <li>
              <Trans i18nKey="privacy.s6.li1" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li2" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li3" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li4" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li5" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li6" components={{ b: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="privacy.s6.li7" components={{ b: <strong /> }} />
            </li>
          </ul>
          <P>
            <Trans
              i18nKey="privacy.s6.outro"
              components={{
                a: <ExtA href="mailto:fh@brain-hr.com">fh@brain-hr.com</ExtA>,
              }}
            />
          </P>
        </Section>

        <Section title={t("privacy.s7.title")}>
          <P>{t("privacy.s7.body")}</P>
          <address className="not-italic">
            Landesbeauftragte für Datenschutz und Informationsfreiheit
            Nordrhein-Westfalen
            <br />
            Postfach 20 04 44, 40102 Düsseldorf
            <br />
            Web:{" "}
            <ExtA href="https://www.ldi.nrw.de" external>
              www.ldi.nrw.de
            </ExtA>
          </address>
        </Section>

        <Section title={t("privacy.s8.title")}>
          <P>
            <Trans i18nKey="privacy.s8.body" components={{ em: <em /> }} />
          </P>
        </Section>

        <Section title={t("privacy.s9.title")}>
          <P>{t("privacy.s9.body")}</P>
        </Section>

        <Section title={t("privacy.s10.title")}>
          <P>{t("privacy.s10.body")}</P>
        </Section>

        <p className="text-ca-ink-muted text-xs mt-12">
          {t("privacy.version", { version: PRIVACY_VERSION })}
        </p>

        <footer className="mt-12 pt-6 border-t border-ca-border text-xs text-ca-ink-muted flex gap-4">
          <Link to="/" className="hover:text-ca-accent-text">
            Nudging Bloom
          </Link>
          <Link to="/datenschutz" className="hover:text-ca-accent-text">
            {t("auth.privacyLink")}
          </Link>
        </footer>
      </div>
    </div>
  );
}

// --- helpers ---

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xs uppercase tracking-[0.18em] text-brand-gold font-bold mb-3">
        {title}
      </h2>
      <div className="space-y-2 text-ca-ink">{children}</div>
    </section>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-ca-ink mt-4 mb-1">{children}</h3>;
}

function P({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>;
}

function Tr({
  a,
  b,
  c,
}: {
  a: React.ReactNode;
  b: React.ReactNode;
  c: React.ReactNode;
}) {
  return (
    <tr>
      <td className="py-2 pr-3 border-b border-ca-border align-top">{a}</td>
      <td className="py-2 pr-3 border-b border-ca-border align-top">{b}</td>
      <td className="py-2 border-b border-ca-border align-top">{c}</td>
    </tr>
  );
}

function ExtA({
  href,
  external,
  children,
}: {
  href: string;
  external?: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      className="text-ca-accent-text hover:underline"
      {...(external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {children}
    </a>
  );
}
